import { FocusScope } from '@react-aria/focus';
import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from 'react';
import FocusLock from 'react-focus-lock';

import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { Overlay } from '@trello/nachos/overlay';
import { RouteId, useRouteId } from '@trello/router';

import {
  closeGAAnnouncementModal,
  useIsGAAnnouncementModalOpen,
} from '../betaGASharedState';
import { usePersonalProductivityCohortValue } from '../usePersonalProductivityCohortValue';
import { GAAnnouncementModalWithOptIn } from './GAAnnouncementModalWithOptIn';
import { GAAnnouncementModalWithoutOptIn } from './GAAnnouncementModalWithoutOptIn';

import styles from './GAAnnouncementModal.less';

export const GAAnnouncementModal: FunctionComponent = () => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { value: isGateEnabled } = useFeatureGate(
    'trello_beta_ga_announcement',
  );
  const { value: isReactFocusLockEnabled } = useFeatureGate(
    'ghost_use_react_focus_lock',
  );
  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();
  const hasSeenAnnouncementModal = isOneTimeMessageDismissed(
    'beta-ga-announcement-modal',
  );
  const shouldShowAnnouncementModalAgain = useIsGAAnnouncementModalOpen();
  const personalProductivityCohort = usePersonalProductivityCohortValue();
  const isOptedIn = personalProductivityCohort === 'opted_in';
  const routeId = useRouteId();
  const isBoardRoute = routeId === RouteId.BOARD;

  const shouldShowModal =
    ((isModalOpen && !hasSeenAnnouncementModal) ||
      shouldShowAnnouncementModalAgain) &&
    isGateEnabled &&
    isBoardRoute;

  const onClose = useCallback(async () => {
    await dismissOneTimeMessage('beta-ga-announcement-modal');
    setIsModalOpen(false);
    closeGAAnnouncementModal();
  }, [dismissOneTimeMessage]);

  useEffect(() => {
    if (shouldShowModal) {
      Analytics.sendViewedComponentEvent({
        componentType: 'modal',
        componentName: 'gaAnnouncementModal',
        source: 'boardScreen',
        attributes: {
          cohort: personalProductivityCohort,
        },
      });
    }
  }, [personalProductivityCohort, shouldShowModal]);

  const ModalComponent = isOptedIn
    ? GAAnnouncementModalWithoutOptIn
    : GAAnnouncementModalWithOptIn;

  const FocusComponent = isReactFocusLockEnabled ? FocusLock : FocusScope;

  const focusProps = isReactFocusLockEnabled
    ? { returnFocus: true, autoFocus: true }
    : { contain: true, returnFocus: true, autoFocus: true };

  return (
    shouldShowModal && (
      <Overlay
        className={styles.overlay}
        closeOnEscape={true}
        closeOnOutsideClick={false}
        onClose={onClose}
      >
        <FocusComponent {...focusProps}>
          <ModalComponent onClose={onClose} />
        </FocusComponent>
      </Overlay>
    )
  );
};
